import { BookingFormState, State, getDefaultState } from './state';
import {
  GoodInput,
  GoodsMode,
  GoodsType,
  KeyInput,
  PickupType,
  SectionName,
  StoreContactType,
} from '@/config/constants';
import { IntegraAccountSettings, Tariff } from '@/api/integraLink/types';
import { createPinia, defineStore } from 'pinia';
import {
  getLocationPermissionState,
  requestLocationPermission,
} from '@/utils/permissions';

import { Contact } from '@/api/freedomQl/__generated__/graphql';
import { Geolocation } from '@capacitor/geolocation';
import { PiniaLogger } from 'pinia-logger';
import { createLocalForagePlugin } from './persist';
import { getAccountWebSettings } from '@/services/accountWebSettings';
import { getAppData, getQuickQuoteAvailable } from '@/api/freedomQl';
import { errorDetails } from '@/services/logger/errorDetails';
import logger from '@/services/logger';
import { parseMobileNumber } from '@/utils/helpers';

export const initialisePinia = () => {
  // PiniaLogger Defaults are:
  // const defaultOptions = {
  //   logErrors: true,
  //   disabled: false,
  //   expanded: true,
  //   showStoreName: true,
  //   showDuration: false,
  //   showTime: true,
  //   filter: () => true
  //   actions: undefined
  // }

  return createPinia()
    .use(
      PiniaLogger({
        expanded: true,
        disabled: import.meta.env.MODE !== 'development',
        // use a filter to only log certain actions
        //filter: ({ name }) => name !== 'incrementCounter',
        // alternatively, you can specify the actions you want to log
        // if undefined, all actions will be logged
        //actions: ['decrementCounter'],
      }),
    )
    .use(createLocalForagePlugin());
};

export const useStore = defineStore('appStore', {
  state: (): State => getDefaultState(),
  getters: {
    // A guest session (quick quote) is logged in to Integra but has no account
    isSignedIntoIntegra(state): boolean {
      return !!state.integraProfile?.loggedIn && !state.isGuestSession;
    },
    isJobMasquerading(state) {
      return (contactOverride?: Contact) => {
        const contactToCheck = contactOverride || state.jobContact;
        return (
          state.integraProfile.user.contactRef !== +contactToCheck.reference
        );
      };
    },
    isProfileMasquerading(state) {
      return (contactOverride?: Contact) => {
        const contactToCheck = contactOverride || state.profileContact;
        return (
          state.integraProfile.user?.contactRef !== +contactToCheck.reference
        );
      };
    },
    getGoodsCount(state) {
      return (type: KeyInput.GOODS_QTY | KeyInput.GOODS_CARTON) => {
        const { bookingForm } = state.activeBooking;
        if (!bookingForm) return 0;

        const isGoodsQty = type === KeyInput.GOODS_QTY;
        switch (this.getGoodsMode) {
          case GoodsMode.GLOBAL:
            return bookingForm[type] ?? 0;

          case GoodsMode.PER_GOOD:
            return (
              bookingForm.goods?.reduce(
                (sum, good) =>
                  sum + ((isGoodsQty ? good.quantity : good.unitCartons) || 0),
                0,
              ) ?? 0
            );

          default:
            return 0;
        }
      };
    },
    getGoodsCountLimits(state) {
      return (
        type: KeyInput.GOODS_QTY | KeyInput.GOODS_CARTON,
      ): { min: number; max: number } => {
        const { bookingTemplate } = state.activeBooking;
        if (!bookingTemplate) return { min: 0, max: 0 };

        const goodsInputs = bookingTemplate.sections.find(
          (s) => s.name === SectionName.GOODS,
        )?.inputs;

        if (!goodsInputs) return { min: 0, max: 0 };

        const isGoodsQty = type === KeyInput.GOODS_QTY;

        let input;
        switch (this.getGoodsMode) {
          case GoodsMode.GLOBAL:
            input = goodsInputs.find((i) => i.dataName === type);
            break;
          case GoodsMode.PER_GOOD:
            input = goodsInputs.find(
              (i) =>
                i.dataName ===
                (isGoodsQty ? GoodInput.QUANTITY : GoodInput.UNIT_CARTONS),
            );
            break;
          default:
            return { min: 0, max: 0 };
        }

        return {
          min: input?.min ?? 0,
          max: input?.max ?? 0,
        };
      };
    },
    getAvailableTariffs(state) {
      return (sortFn?: (a: Tariff, b: Tariff) => number) => {
        const { bookingForm, tariffs } = state.activeBooking;
        const filteredTariffs = tariffs?.reduce((result, t) => {
          if (
            !t.maxGoodsQty ||
            this.getGoodsCount(KeyInput.GOODS_QTY) <= t.maxGoodsQty
          ) {
            if (
              !bookingForm?.goodsCarton ||
              !t.maxGoodsCartons ||
              bookingForm?.goodsCarton <= t.maxGoodsCartons
            ) {
              result.push(t);
            }
          }
          return result;
        }, [] as Tariff[]);

        return (
          filteredTariffs?.sort(
            sortFn ?? ((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
          ) ?? []
        );
      };
    },
    getParameter(state) {
      return (code: string) => {
        return (
          state.appData.parameters.find((parameter) => parameter.code === code)
            ?.setting ?? ''
        );
      };
    },
    getCancellationReasons(state) {
      const reasons =
        state.appData.parameters.find(
          (parameter) => parameter.code === 'INTEGRA_CANCREASONS',
        )?.setting ?? '';
      return reasons.split('\r');
    },
    getAccountSetting:
      (state) =>
      (
        contactType: StoreContactType,
        setting: keyof IntegraAccountSettings,
      ) => {
        const contact =
          contactType === StoreContactType.JOB
            ? state.jobContact
            : state.profileContact;
        return contact?.accountWebSettings?.[setting];
      },
    getPickupType(state) {
      if (state.activeBooking.bookingForm?.asap) {
        return PickupType.ASAP;
      } else if (state.activeBooking.bookingForm?.arriveBy) {
        return PickupType.ARRIVE_BY;
      } else {
        return PickupType.PICKUP_AT;
      }
    },
    getGoodsMode(state) {
      return state.activeBooking.bookingTemplate?.sections
        .find((s) => s.name === SectionName.GOODS)
        ?.inputs?.find((i) => i.dataName === KeyInput.GOODS_QTY)
        ? GoodsMode.GLOBAL
        : GoodsMode.PER_GOOD;
    },
  },
  actions: {
    resetStore(clearMFA = true) {
      const rememberEmail = this.signInDetails.rememberedEmail;
      const mfaDetails = this.mfaDetails;
      const cookieConsent = this.cookieConsent;
      const quickQuote = this.quickQuote;
      const ssoBooking = this.ssoBooking;
      const authProxyToken = this.authProxyToken;
      const quickQuoteAvailable = this.quickQuoteAvailable;
      const locale = this.locale;
      const themePreference = this.themePreference;

      Object.assign(this, getDefaultState());
      this.signInDetails.rememberedEmail = rememberEmail;
      this.cookieConsent = cookieConsent;
      this.quickQuote = quickQuote;
      this.ssoBooking = ssoBooking;
      this.authProxyToken = authProxyToken;
      this.quickQuoteAvailable = quickQuoteAvailable;
      this.locale = locale;
      this.themePreference = themePreference;

      if (clearMFA) {
        return;
      }

      this.mfaDetails = mfaDetails;
    },
    incrementWrongPassCount() {
      this.mfaDetails.wrongPassCount++;
    },
    resetMasqueradeSearchContacts() {
      if (this.appDetails.bookFor?.useSearch) {
        this.appDetails.bookFor.contacts = [];
      }

      if (this.appDetails.masqueradeAndMaintain?.useSearch) {
        this.appDetails.masqueradeAndMaintain.contacts = [];
      }
    },
    // Runs in the background before sign in, on installs that may not offer
    // guests anything at all, so a failure is only logged. The last answer is
    // kept until a lookup succeeds, so a blip doesn't hide or show anything.
    async refreshQuickQuoteAvailable() {
      try {
        this.quickQuoteAvailable = await getQuickQuoteAvailable();
      } catch (e: any) {
        logger.warn('refreshQuickQuoteAvailable', errorDetails(e));
      }
    },
    async setupAppData() {
      this.appData = await getAppData(['INTEGRA_CANCREASONS']);
    },
    async setBackgroundLocation() {
      try {
        this.location.permissions = await getLocationPermissionState();

        if (this.location.permissions !== 'granted') {
          await requestLocationPermission();
          return;
        }
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
        });

        this.location.position = position;
        logger.debug('Set background location', { position });
      } catch (e: any) {
        logger.error('setBackgroundLocation', e.message ?? e);
      }
    },
    resetQuotes() {
      this.activeBooking.quotes = [];
      this.activeBooking.quoteError = undefined;
      this.activeBooking.journeyDistance = undefined;
      this.activeBooking.journeyDurationMins = undefined;
    },
    clearBookingValues() {
      this.activeBooking.copyBookingRef = undefined;
      this.activeBooking.returnBookingRef = undefined;
      this.ssoBooking = undefined;
      this.activeBooking.ready = false;
      this.resetQuotes();
    },
    async setContact(
      contactTypes: StoreContactType[] | StoreContactType,
      contact: Contact,
    ) {
      const typesArray = Array.isArray(contactTypes)
        ? contactTypes
        : [contactTypes];

      await Promise.all(
        typesArray.map(async (type) => {
          const isJob = type === StoreContactType.JOB;
          const contactProperty = isJob ? 'jobContact' : 'profileContact';
          const masqueradingGetter = isJob
            ? 'isJobMasquerading'
            : 'isProfileMasquerading';

          const accountWebSettings = await getAccountWebSettings(
            contact,
            this[masqueradingGetter](contact),
          );

          this[contactProperty] = {
            ...contact,
            accountWebSettings,
          };
        }),
      );
    },
    setQuickQuoteState(formValues: BookingFormState, goodsType: GoodsType) {
      this.quickQuote = { formValues, goodsType };

      if (formValues.contact) {
        const { name, mobile, email } = formValues.contact;
        const { country, number } = parseMobileNumber(mobile);
        this.signInDetails.defaults = {
          email,
          mobile: number,
          name,
          country,
        };
      }

      logger.info('setQuickQuoteState', {
        goodsType,
        hasContact: !!formValues.contact,
      });
    },
  },
});
